import React from "react";
import { CurrentUserProvider } from "../../components/routes/CurrentUserProvider";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { UserInfo } from "../../hooks/useCurrentUser";
import useAuth from "../../hooks/auth/useAuth";
import FbUsersService from "../../services/FbUsersService";
import {
  ACTIVITY_ROUTE,
  CLIENT_PORTAL_ROUTE,
  CLIENT_PROFILE_ROUTE,
  GROUPS_ROUTE,
  HOME_ROUTE,
  MESSAGES_ROUTE,
  PROGRAMS_ROUTE,
} from "../../routes/routes";
import {
  Outlet,
  useLocation,
  matchRoutes,
  useSearchParams,
} from "react-router-dom";
import { WorkspaceDto } from "@growth-machine-llc/stridist-api-client";
import { colorSystem } from "../../theme";
import WorkspacesService from "../../services/WorkspacesService";
import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";
import { brandedAppWorkspaceKey } from "../../utils/mobile";
import { DefaultLoader } from "../../components/loading/DefaultLoader";
import {
  deleteCookie,
  getCookie,
  setCookie,
} from "../../components/program-component/utils";
import { TeamRole, UserRole } from "../../constants";

interface CurrentUserWrapperProps {
  authorized: boolean;
  children?: React.JSX.Element;
}

export const CURRENT_USER_QUERY_KEY = "current-user";
export const CURRENT_WORKSPACE_QUERY_KEY = "get-workspace";

const ignoreWorkspaceRoutes = [
  HOME_ROUTE,
  PROGRAMS_ROUTE,
  GROUPS_ROUTE,
  ACTIVITY_ROUTE,
  MESSAGES_ROUTE,
  CLIENT_PROFILE_ROUTE,
];

// TODO_API_V2: Consider about default workspace
const defaultWorkspace = new WorkspaceDto({
  primaryColor: colorSystem.primary,
  slug: "",
  name: "",
  logoUrl: "",
});

interface CurrentUserWrapperProps extends React.PropsWithChildren<{}> {
  authorized: boolean;
}

export const CurrentUserWrapper = ({
  authorized,
  children,
}: CurrentUserWrapperProps) => {
  const { userRole } = useAuth();
  const location = useLocation();
  const matches = matchRoutes([{ path: CLIENT_PORTAL_ROUTE }], location);

  const [searchParams] = useSearchParams();
  const setWorkspaceCookieParam = searchParams.get("setWorkspaceCookie");

  const { isBrandedApp } = useCurrentBrand();

  const cookieFallback = getCookie("workspace-name");
  const seachParamsFallback1 = searchParams.get("workspace");
  const seachParamsFallback2 = searchParams.get("workspaceSlug");

  const workspaceFallback =
    setWorkspaceCookieParam ||
    cookieFallback ||
    seachParamsFallback1 ||
    seachParamsFallback2;

  const workspaceSlug = isBrandedApp
    ? brandedAppWorkspaceKey
    : workspaceFallback
      ? workspaceFallback
      : matches && matches[0]?.params?.workspace;

  // To distinguish :workspace param from just one word route
  // we forced to add all of them to`ignoreWorkspaceRoutes`.
  // This is needed to retrieve slug from coach workspace URL when user
  // is not logged and the only source of custom theme and logo is data in URL
  const isWorkspace = isBrandedApp
    ? isBrandedApp
    : !ignoreWorkspaceRoutes.includes("/" + workspaceSlug);

  const { data: user, isLoading: currentUserLoading } = useQuery<
    UserInfo | undefined
  >({
    queryKey: [CURRENT_USER_QUERY_KEY],
    queryFn: async () => {
      const u = await FbUsersService.getCurrentUser();
      return u
        ? ({ ...u, teamRole: u.teamRole as TeamRole } as UserInfo)
        : undefined;
    },
    // NOTE: `get-current-user` enabled only for authorized users to get user data and workspace
    enabled: authorized,
    refetchOnWindowFocus: false,
    refetchInterval: 1000 * 60 * 5, // 5 minutes
  });

  const { data: workspaceData, isLoading: currentWorkspaceLoading } = useQuery({
    queryKey: [CURRENT_WORKSPACE_QUERY_KEY],
    queryFn: () => WorkspacesService.getWorkspace(workspaceSlug),
    // NOTE: `get-workspace` query enabled only for unauthorized users to get custom workspace.
    // Get workspace info for unauthorized users with false 'isWorkspace' if about to set the workspace cookie.
    // Generally, this cookie should be set only for branded app clients that using the Stridist
    // via browser instead of the branded app.
    enabled:
      !authorized &&
      (isWorkspace || setWorkspaceCookieParam) &&
      !!workspaceSlug,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    select: (data) => data?.workspace,
  });

  const workspace = workspaceData || defaultWorkspace;

  const workspaceHasBrandedApp = workspace?.brandedApp?.status === "Published";

  React.useEffect(() => {
    // Cookie is set to ensure branded app clients would see branded login form.
    if (setWorkspaceCookieParam && workspaceHasBrandedApp && !authorized) {
      setCookie("workspace-name", setWorkspaceCookieParam);
    }
  }, [setWorkspaceCookieParam, workspaceHasBrandedApp, authorized]);

  React.useEffect(() => {
    // workspace cookie is cleaned for coaches so they can access Stridist sign in page
    // with additional coach options like "Sign up as a coach".
    if (authorized && userRole === UserRole.COACH && cookieFallback) {
      deleteCookie("workspace-name");
    }
  }, [cookieFallback, userRole, authorized]);

  const myWorkspace = user?.workspace || workspace;

  return (
    <CurrentUserProvider me={user} workspace={myWorkspace}>
      {currentUserLoading || currentWorkspaceLoading ? (
        <DefaultLoader fillScreen />
      ) : (
        children || <Outlet />
      )}
    </CurrentUserProvider>
  );
};
